/**
 * utilities/analytics.ts
 * desc: interaction tracking, including page views and user clicks
 * note: compliance forbids tracking Personally identifiable information (PII)
 */

/* istanbul ignore file */

import { capitalizeEachWord } from './index';
import type { AppStore } from '../store';
import type { UsersState } from '../store/reducers/users';

// the store registers itself via setAnalyticsStore instead of being imported —
// an import here closes the store → api → error → analytics → store cycle and
// leaves `api` undefined when the graph is entered through store/api
let store: Pick<AppStore, 'getState'> | null = null;

// the error shape trackError consumes (ApiRequestError re-formats itself into
// this before calling)
interface TrackedError {
    context?: Record<string, unknown> | null;
    id?: string | null;
    name?: string;
    message?: string;
}



/**
 * setAnalyticsStore
 * desc: register the store analytics reads the current user from
 * note: called once by store/index.ts right after the store is created
 */
export function setAnalyticsStore(appStore: Pick<AppStore, 'getState'>): void {
    store = appStore;
}


/**
 * trackError
 * desc: application error tracking
 * note: collects data for general js errors and custom api errors
 * @param {string} action  Action text
 */
export function trackError(err: TrackedError | null | undefined): void {
    const gtag = window.gtag || null;
    if (gtag && typeof err === 'object') {
        // null passes the typeof guard (typeof null === 'object') and crashes
        // below — known quirk, kept deliberately
        const action = _getErrorAction(err as TrackedError);
        gtag('event', action, {
            event_category: 'Error',
            event_action: action,
            event_label: _getErrorLabel(err as TrackedError),
        });
    }
};


/**
 * trackEvent
 * desc: user interaction tracking
 * note: category and label are automatic while action format should be...
 *         syntax: (${COMPONENT_NAME} - )${USER_INTERACTION}
 *       examples: "Definition - Edit Step Name"
 *                 "Open Path"
 * @param {string} action  Action text
 */
export function trackEvent(action: string): void {
    const gtag = window.gtag || null;
    if (gtag && action) {
        const labelData = _getEventLabelData();
        gtag('event', action, {
            event_category: _getEventCategory(),
            event_action: action,
            event_label: labelData.str,
            ...labelData.obj,
        });
    }
};


/**
 * trackPage
 * desc: track the page url
 * note: since the SPA uses pushstate we invoke this method
 */
export function trackPage(): void {
    const gtag = window.gtag || null;
    if (gtag) {
        gtag('event', 'page_view', {
            page_title: window.document.title,
            page_location: window.location.href.split('?')[0],
        });
    }
};


/**
 * _getEventCategory
 * desc: converts url path into a readable feature name
 * note: top-level descriptor used for event organization
 * ex: "/journeys/0e017677-447c-432d-8fc8-57f9968c3406" -> "Journey Detail"
 * @return {string}  Formatted category text
 */
function _getEventCategory(): string {
    const pathname = window.location.pathname.replace(/^\//, '');
    const pathParts = pathname.split('/');
    const pathText = pathParts.map(pathPart => {
        const uuidRegEx = /\b[a-f0-9]{8}\b-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-\b[a-f0-9]{12}\b/;
        const isUuid = uuidRegEx.test(pathPart);
        const textNoDashes = pathPart.replace('-', ' ');
        return isUuid ? 'Detail' : capitalizeEachWord(textNoDashes);
    });

    return pathText.join(' ');
};


/**
 * _getErrorAction
 * desc: error action text
 * note: catches general js errors and custom api errors
 * @param  {object} err  Error object
 * @return {string}      Formatted action text
 */
function _getErrorAction(err: TrackedError): string {
    const statusCode = err.context?.statusCode as number | undefined;
    let actionText = `${err.name}: ${err.message}`;

    // if status code exists, then prepend to action text
    if (statusCode) {
        actionText = `${statusCode} ${actionText}`;
    }

    return actionText;
};


/**
 * _getErrorLabel
 * desc: error label text
 * note: only custom api errors will have an
 * @param  {object} err  Error object
 * @return {string}      Formatted label text
 */
function _getErrorLabel(err: TrackedError): string {
    let labelText = _getEventLabelData().str;

    // if error id exists, then append to label text
    // note: only custom api errors will have an unique identifier
    if (err.id) {
        labelText = `${labelText} | errorId=${err.id}`;
    }

    return labelText;
};


/**
 * _getEventLabelData
 * desc: supplemental information for troubleshooting
 * note: str is organized macro -> micro to help w sorting
 * @return {object}  Includes two data types (str and obj)
 */
function _getEventLabelData(): { obj: Record<string, unknown>; str: string } {
    const users: Pick<UsersState, 'me'> = store ? store.getState().users : { me: {} };
    const appVersion = window.APP_VERSION;
    const clientId = _getClientId();
    const pageUrl = window.location.pathname;
    const userId = _getUserId(users);
    const userLocale = _getUserLocale(users);
    const userPermissions = _getUserPermissions(users);
    const userRoles = _getUserRoles(users);

    return {
        obj: { appVersion, clientId, userId, userRoles, userPermissions, userLocale, pageUrl },
        str: `appVersion=${appVersion} | clientId=${clientId} | userId=${userId} | userRoles=${userRoles} | userPermissions=${userPermissions} | locale=${userLocale} | url=${pageUrl}`,
    };
};


/**
 * _getClientId
 * desc: convience function to get clientId
 * note: use env config, otherwise fallback to subdomain
 * @param  {object} users  Users slice of the store
 * @return {*}             UserId || null
 */
function _getClientId(): string {
    const subdomain = window.location.hostname.split('.')[0];
    return window.Ym.CLIENT_ID || subdomain;
};


/**
 * _getUserId
 * desc: convience function to get userId
 * note: user is set to null in initial state
 * @param  {object} users  Users slice of the store
 * @return {*}             UserId || null
 */
function _getUserId(users: Pick<UsersState, 'me'>): string | null {
    const user = users.me;
    return user?.userDto?.id || null;
};


/**
 * _getUserLocale
 * desc: convience function to get user locale
 * note: user is set to null in initial state
 * @param  {object} users  Users slice of the store
 * @return {*}             UserId || null
 */
function _getUserLocale(users: Pick<UsersState, 'me'>): string | null {
    const user = users.me;
    return user?.userDto?.languageTag || null;
};


/**
 * _getUserPermissions
 * desc: convience function to get list of user permissions
 * note: user=null and permissions=undefined in initial state
 * @param  {object} users  Users slice of the store
 * @return {*}             User permissions || null
 */
function _getUserPermissions(users: Pick<UsersState, 'me'>): string | null {
    const user = users.me;
    const permissions = user?.permissions;
    return Array.isArray(permissions) ? permissions.join(',') : null;
};


/**
 * _getUserRoles
 * desc: convience function to get list of user roles
 * note: user=null and roles=undefined in initial state
 * @param  {object} users  Users slice of the store
 * @return {*}             User Roles || null
 */
function _getUserRoles(users: Pick<UsersState, 'me'>): string | null {
    const user = users.me;
    const roles = user?.roles;
    return Array.isArray(roles) ? roles.join(',') : null;
};
